import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Logo from '../components/Logo';

const Login = ({ type = 'cms' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('employee'); // For employee portal login
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      const { user } = result;
      
      // Route based on user role and login type
      if (type === 'admin' && user.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (type === 'employee') {
        // Validate that the user's actual role matches selected role
        if (user.role !== selectedRole) {
          setError(`Access denied. You are trying to login as ${selectedRole.toUpperCase()} but your account is ${user.role.toUpperCase()}.`);
          setLoading(false);
          return;
        }
        navigate('/employee/dashboard');
      } else if (type === 'admin') {
        setError('Admin access only. Please use Employee Portal for employee login.');
        setLoading(false);
      } else {
        setError('Unauthorized access');
        setLoading(false);
      }
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary via-primary/90 to-secondary/20 px-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-4 pb-8">
          <div className="flex justify-center">
            <Logo />
          </div>
          <CardTitle className="text-2xl text-center">
            {type === 'admin' ? 'Admin Login' : 'Employee Portal Login'}
          </CardTitle>
          <p className="text-center text-sm text-gray-600">
            {type === 'admin' 
              ? 'Admin access only' 
              : 'Employee & HR access'
            }
          </p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            
            {type === 'employee' && (
              <div>
                <label className="block text-sm font-medium mb-3">I am logging in as:</label>
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="employee"
                      checked={selectedRole === 'employee'}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                      disabled={loading}
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Employee</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="hr"
                      checked={selectedRole === 'hr'}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                      disabled={loading}
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">HR</span>
                  </label>
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={loading}
                className="border-gray-300 focus:border-primary focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
                className="border-gray-300 focus:border-primary focus:ring-primary"
              />
            </div>
            
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-white h-12 text-lg"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            
            <div className="text-center text-sm">
              <button
                type="button"
                onClick={() => navigate(type === 'admin' ? '/admin/forgot-password' : '/employee/forgot-password')}
                className="text-primary hover:underline"
              >
                Forgot Password?
              </button>
            </div>
            
            <div className="text-center text-sm text-gray-600 mt-2">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-primary hover:underline"
              >
                ← Back to Home
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
